/**
 * Build matrix (pivot) table rows for Excel/PDF export — matches MatrixReportTable layout.
 */

import { buildMeasuresFromSummarized } from './matrixReportConfig';
import { buildMatrixGrid, resolveRowTotalValue } from './matrixPivot';
import { formatMatrixMeasureCell } from './matrixCurrencyFormat';

/**
 * @returns {{ aoa: string[][], merges: Array<{ r: number, c: number, rowspan?: number, colspan?: number }> }}
 */
export function buildMatrixExportTable({
  previewData = [],
  rowGroups = [],
  columnGroups = [],
  selectedFields = [],
  summarizedFields = {},
  matrixDateGranularity = 'month',
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  multiCurrencyEnabled = true,
  matrixGrid = null,
}) {
  const measures = buildMeasuresFromSummarized(selectedFields, summarizedFields);
  const currencyOptions = {
    baseCurrencyCode: multiCurrencyEnabled ? baseCurrencyCode : null,
    exchangeRateLookup: multiCurrencyEnabled ? exchangeRateLookup : null,
  };
  const pivotOptions = {
    matrixDateGranularity,
    reportFields: selectedFields,
    ...currencyOptions,
  };

  const grid =
    matrixGrid?.rowKeys?.length
      ? matrixGrid
      : buildMatrixGrid(
          previewData,
          rowGroups,
          columnGroups,
          measures,
          pivotOptions
        );

  if (!grid?.rowKeys?.length) {
    return { aoa: [], merges: [], headRowCount: 0 };
  }

  const formatCell = (value, measure) =>
    formatMatrixMeasureCell(value, measure, measure.aggregation, currencyOptions);

  const { rowGroupFields, measures: measureList, rowLabels, columnLabels } = grid;
  const multiMeasure = measureList.length > 1;
  const aoa = [];
  const merges = [];

  const pushMerge = (r, c, rowspan = 1, colspan = 1) => {
    merges.push({ r, c, rowspan, colspan });
  };

  const headerRowIndex = aoa.length;

  const h1 = rowGroupFields.map(f => f.field_label || f.field_name);
  const rowHdrCols = h1.length;

  columnLabels.forEach(col => {
    if (multiMeasure) {
      const startCol = h1.length;
      h1.push(col.label);
      for (let i = 1; i < measureList.length; i++) h1.push('');
      pushMerge(headerRowIndex, startCol, 1, measureList.length);
    } else {
      h1.push(col.label);
    }
  });

  if (multiMeasure) {
    const totalStart = h1.length;
    h1.push('Total');
    for (let i = 1; i < measureList.length; i++) h1.push('');
    pushMerge(headerRowIndex, totalStart, 2, measureList.length);
    rowGroupFields.forEach((_, i) => {
      pushMerge(headerRowIndex, i, 2, 1);
    });
  } else {
    h1.push('Total');
  }

  aoa.push(h1);

  if (multiMeasure) {
    const h2 = rowGroupFields.map(() => '');
    columnLabels.forEach(() => {
      measureList.forEach(m => {
        h2.push(m.field_label || m.field_name);
      });
    });
    measureList.forEach(() => h2.push(''));
    aoa.push(h2);
  }

  const headRowCount = aoa.length;

  rowLabels.forEach(rowLabel => {
    const row = [];
    rowGroupFields.forEach((f, idx) => {
      row.push(
        rowLabel.parts[idx] ??
          (idx === 0 && rowLabel.key === '__all__' ? 'All' : '')
      );
    });
    columnLabels.forEach(col => {
      measureList.forEach(measure => {
        const mid = measure.id || measure.field_name;
        const value = grid.cells[rowLabel.key]?.[col.key]?.[mid];
        row.push(formatCell(value, measure));
      });
    });
    measureList.forEach(measure => {
      const value = resolveRowTotalValue(grid, rowLabel.key, measure);
      row.push(formatCell(value, measure));
    });
    aoa.push(row);
  });

  const grandRow = [];
  rowGroupFields.forEach((f, idx) => {
    grandRow.push(idx === 0 ? 'Grand Total' : '');
  });
  columnLabels.forEach(col => {
    measureList.forEach(measure => {
      const mid = measure.id || measure.field_name;
      const value = grid.columnTotalCells[col.key]?.[mid];
      grandRow.push(formatCell(value, measure));
    });
  });
  measureList.forEach(measure => {
    const mid = measure.id || measure.field_name;
    const value = grid.grandTotalCells[mid];
    grandRow.push(formatCell(value, measure));
  });
  aoa.push(grandRow);

  return { aoa, merges, headRowCount, measureList, rowHdrCols };
}

/** Apply merge ranges to an xlsx worksheet (!merges). */
export function applyMatrixMergesToWorksheet(worksheet, merges, rowOffset = 0) {
  if (!worksheet || !merges?.length) return;
  worksheet['!merges'] = worksheet['!merges'] || [];
  for (const m of merges) {
    const r0 = rowOffset + m.r;
    const c0 = m.c;
    const r1 = r0 + (m.rowspan || 1) - 1;
    const c1 = c0 + (m.colspan || 1) - 1;
    worksheet['!merges'].push({
      s: { r: r0, c: c0 },
      e: { r: r1, c: c1 },
    });
  }
}

export function splitMatrixExportHeadBody(aoa, headRowCount) {
  if (!aoa?.length || headRowCount <= 0) {
    return { headRows: [], bodyRows: aoa || [] };
  }
  return {
    headRows: aoa.slice(0, headRowCount),
    bodyRows: aoa.slice(headRowCount),
  };
}
